import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../api/firebase';

interface AccessCodeValidation {
    valid: boolean;
    discountAmount: number;
    code: string;
}

interface UseAccessCodeReturn {
    validateCode: (code: string) => Promise<AccessCodeValidation | null>;
    isValidating: boolean;
    error: string | null;
    clearError: () => void;
}

export const useAccessCode = (): UseAccessCodeReturn => {
    const [isValidating, setIsValidating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const validateCode = useCallback(async (code: string): Promise<AccessCodeValidation | null> => {
        if (!code.trim()) {
            setError('Please enter an access code');
            return null;
        }

        setIsValidating(true);
        setError(null);

        try {
            const validateFunction = httpsCallable(functions, 'validateAccessCode');
            const result = await validateFunction({ code: code.trim() });
            const data = result.data as AccessCodeValidation;

            if (data.valid) {
                return data;
            } else {
                setError('Invalid access code');
                return null;
            }
        } catch (error: any) {
            console.log('Firebase function error:', error);
            // Handle specific error codes from the cloud function
            if (error.code === 'not-found') {
                setError('Invalid access code');
            } else if (error.code === 'failed-precondition') {
                setError('This access code has expired');
            } else if (error.code === 'resource-exhausted') {
                setError('This access code has reached its usage limit');
            } else {
                setError(error.message || 'Failed to validate access code');
            }
            return null;
        } finally {
            setIsValidating(false);
        }
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        validateCode,
        isValidating,
        error,
        clearError
    };
};